import { Button } from '@repo/shadcn';
import { ColumnDef, Row } from '@tanstack/react-table';
import { FC } from 'react';
import { FaEdit } from 'react-icons/fa';
import { TbTrash } from 'react-icons/tb';
import { toast } from 'sonner';
import { z } from 'zod';

import { MODAL_ROUTE } from '@/constants';
import { useAppDispatch } from '@/features';
import { openModal } from '@/features/slice/event/event-slice';
import { useDeleteExpenseMutation } from '@/features/slice/expense/expense-api-slice';
import { SingleExpenseResponseSchema } from '@/features/slice/expense/expense.schema';
import { formatCurrencyInBDT } from '@/utils/currency-formater';

type ActionProps = {
    row: Row<z.infer<typeof SingleExpenseResponseSchema>>;
};
const Actions: FC<ActionProps> = ({ row }) => {
    const [deleteExpense] = useDeleteExpenseMutation();
    const dispatch = useAppDispatch();

    const handleDelete = async (id: string) => {
        try {
            await deleteExpense(id).unwrap();
            toast.success('Expense deleted successfully');
        } catch (error) {
            console.error('Failed to delete expense:', error);
            toast.error('Failed to delete expense');
        }
    };

    const handleUpdate = () => {
        dispatch(
            openModal({
                content: MODAL_ROUTE.CREATE_EXPENSE,
                data: row.original,
            }),
        );
    };

    return (
        <div className="flex items-center justify-end gap-2">
            <Button
                className="group !p-1"
                type="button"
                variant={'link'}
                size="icon"
                onClick={() => {
                    handleUpdate();
                }}
            >
                <FaEdit className="h-5 w-5 text-gray-600 group-hover:text-gray-900" />
            </Button>
            <Button
                className="group !p-1"
                type="button"
                variant={'link'}
                size="icon"
                onClick={() => {
                    handleDelete(row.original.id);
                }}
            >
                <TbTrash className="h-5 w-5 text-red-800 group-hover:text-red-500" />
            </Button>
        </div>
    );
};

export const expenseColumns: ColumnDef<
    z.infer<typeof SingleExpenseResponseSchema>
>[] = [
    {
        accessorKey: 'created_at',
        header: 'Time',
        cell: ({ row }) => (
            <div className="font-medium">
                {row.original.created_at
                    ? new Date(row.original.created_at).toLocaleString()
                    : 'N/A'}
            </div>
        ),
    },

    {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
            <div className="font-medium">
                {row.original.description || 'N/A'}
            </div>
        ),
    },

    {
        accessorKey: 'category_name',
        header: 'Category',
        cell: ({ row }) => (
            <div className="font-medium">
                {row.original.category_name || 'N/A'}
            </div>
        ),
    },

    {
        accessorKey: 'amount',
        header: 'Amount',
        cell: ({ row }) => (
            <div className="font-medium">
                {formatCurrencyInBDT(row.original.amount)}
            </div>
        ),
    },
    {
        accessorKey: 'id',
        header: () => <div className="text-right">Actions</div>,
        cell: Actions,
    },
];
